import { useState, useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import { TOAST_ERROR, TOAST_SUCCESS } from '../../../../../utils/constants';
import { useReorderPagesMutation, useReorderSectionsMutation } from '../../../../editCourse/editCourseApi';
import { useCourseStructureActions } from '../../../../editCourse/hooks';
import { useTranslation } from 'react-i18next';

/**
 * useSectionActions - Hook that provides all action handlers for TocMenu sections and pages
 *
 * @param {Object} params
 * @param {string} params.courseId - The course ID
 * @param {Object} params.section - The current section
 * @param {number} params.sectionIndex - Index of this section
 * @param {number} params.totalSections - Total number of sections
 * @param {Array} params.coursePages - All course pages
 * @param {Array} params.courseSections - All course sections
 * @param {number} params.activePageId - Currently active page ID
 * @param {boolean} params.isEditContext - Whether we're in edit context
 * @param {Function} params.onNavigateToPage - Navigation callback
 */
const useSectionActions = ({
  courseId,
  section,
  sectionIndex,
  totalSections,
  coursePages,
  courseSections,
  activePageId,
  isEditContext,
  onNavigateToPage,
  quizEnabled,
}) => {
  const { t } = useTranslation();

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuType, setContextMenuType] = useState(null);
  const [contextMenuTarget, setContextMenuTarget] = useState(null);

  // Moving item state (for visual feedback)
  const [movingItem, setMovingItem] = useState(null);

  // API mutations
  const [reorderPages] = useReorderPagesMutation();
  const [reorderSections] = useReorderSectionsMutation();

  // Structure actions hook
  const structureActions = useCourseStructureActions(courseId, {
    onNavigateToPage,
    allPages: coursePages,
    allSections: courseSections,
    currentPageId: activePageId,
  });

  // ============================================
  // Context Menu Handlers
  // ============================================

  const openSectionContextMenu = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const position = e.clientX
        ? { x: e.clientX, y: e.clientY }
        : { x: e.currentTarget.getBoundingClientRect().right, y: e.currentTarget.getBoundingClientRect().bottom };
      setContextMenu(position);
      setContextMenuType('section');
      setContextMenuTarget(section);
    },
    [section],
  );

  const openPageContextMenu = useCallback((e, page) => {
    e.preventDefault();
    e.stopPropagation();
    const position = e.clientX
      ? { x: e.clientX, y: e.clientY }
      : { x: e.currentTarget.getBoundingClientRect().right, y: e.currentTarget.getBoundingClientRect().bottom };
    setContextMenu(position);
    setContextMenuType('page');
    setContextMenuTarget(page);
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setContextMenuType(null);
    setContextMenuTarget(null);
  }, []);

  // ============================================
  // Section Action Handlers
  // ============================================

  const handleMoveSectionUp = useCallback(async () => {
    if (sectionIndex === 0) return;

    setMovingItem({ id: section.id, direction: 'up', type: 'section' });
    closeContextMenu();

    try {
      const sections = [...courseSections].sort((a, b) => a.order - b.order);
      const reorderedSections = [...sections];
      [reorderedSections[sectionIndex - 1], reorderedSections[sectionIndex]] = [reorderedSections[sectionIndex], reorderedSections[sectionIndex - 1]];

      const sectionIds = reorderedSections.map((s) => s.id);
      await reorderSections({ courseId, sectionIds }).unwrap();
      enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
    } catch (error) {
      console.error('Failed to move section up:', error);
      enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
    } finally {
      setMovingItem(null);
    }
  }, [sectionIndex, courseSections, reorderSections, courseId, t, closeContextMenu, section.id]);

  const handleMoveSectionDown = useCallback(async () => {
    if (sectionIndex === totalSections - 1) return;

    setMovingItem({ id: section.id, direction: 'down', type: 'section' });
    closeContextMenu();

    try {
      const sections = [...courseSections].sort((a, b) => a.order - b.order);
      const reorderedSections = [...sections];
      [reorderedSections[sectionIndex], reorderedSections[sectionIndex + 1]] = [reorderedSections[sectionIndex + 1], reorderedSections[sectionIndex]];

      const sectionIds = reorderedSections.map((s) => s.id);
      await reorderSections({ courseId, sectionIds }).unwrap();
      enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
    } catch (error) {
      console.error('Failed to move section down:', error);
      enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
    } finally {
      setMovingItem(null);
    }
  }, [sectionIndex, totalSections, courseSections, reorderSections, courseId, t, closeContextMenu, section.id]);

  const handleRenameSection = useCallback(() => {
    closeContextMenu();
    structureActions.openRenameSectionDialog(section);
  }, [section, closeContextMenu, structureActions]);

  const handleDeleteSection = useCallback(() => {
    closeContextMenu();
    structureActions.openDeleteSectionDialog(section);
  }, [section, closeContextMenu, structureActions]);

  const handleAddPage = useCallback(() => {
    closeContextMenu();
    const sectionPages = coursePages.filter((p) => p.sectionId === section.id);
    const lastOrder = sectionPages.length > 0 ? Math.max(...sectionPages.map((p) => p.order)) : -1;
    structureActions.handleAddPage(section.id, lastOrder);
  }, [section.id, coursePages, closeContextMenu, structureActions]);

  const handleAddSectionBelow = useCallback(() => {
    closeContextMenu();
    structureActions.handleAddSection(section.order);
  }, [section.order, closeContextMenu, structureActions]);

  const handleAddQuiz = useCallback(() => {
    closeContextMenu();
    structureActions.handleAddQuiz();
  }, [closeContextMenu, structureActions]);

  const handleDuplicateSection = useCallback(() => {
    closeContextMenu();
    structureActions.handleDuplicateSection(section.id);
  }, [section.id, closeContextMenu, structureActions]);

  // ============================================
  // Page Action Handlers
  // ============================================

  const handleRenamePage = useCallback(
    (page) => {
      closeContextMenu();
      structureActions.openRenamePageDialog(page);
    },
    [closeContextMenu, structureActions],
  );

  const handleDeletePage = useCallback(
    (page) => {
      closeContextMenu();
      structureActions.openDeletePageDialog(page);
    },
    [closeContextMenu, structureActions],
  );

  const handleDuplicatePage = useCallback(
    (pageId) => {
      closeContextMenu();
      structureActions.handleDuplicatePage(pageId);
    },
    [closeContextMenu, structureActions],
  );

  const handleMovePageUp = useCallback(
    async (pageId) => {
      const sectionPages = coursePages.filter((p) => p.sectionId === section.id).sort((a, b) => a.order - b.order);
      const pageIndex = sectionPages.findIndex((p) => p.id === pageId);
      if (pageIndex === 0) return;

      setMovingItem({ id: pageId, direction: 'up', type: 'page' });
      closeContextMenu();

      try {
        const reorderedPages = [...sectionPages];
        [reorderedPages[pageIndex - 1], reorderedPages[pageIndex]] = [reorderedPages[pageIndex], reorderedPages[pageIndex - 1]];

        const pageIds = reorderedPages.map((p) => p.id);
        await reorderPages({ sectionId: section.id, pageIds, courseId }).unwrap();
        enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      } catch (error) {
        console.error('Failed to move page up:', error);
        enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
      } finally {
        setMovingItem(null);
      }
    },
    [coursePages, section.id, reorderPages, courseId, t, closeContextMenu],
  );

  const handleMovePageDown = useCallback(
    async (pageId) => {
      const sectionPages = coursePages.filter((p) => p.sectionId === section.id).sort((a, b) => a.order - b.order);
      const pageIndex = sectionPages.findIndex((p) => p.id === pageId);
      if (pageIndex === sectionPages.length - 1) return;

      setMovingItem({ id: pageId, direction: 'down', type: 'page' });
      closeContextMenu();

      try {
        const reorderedPages = [...sectionPages];
        [reorderedPages[pageIndex], reorderedPages[pageIndex + 1]] = [reorderedPages[pageIndex + 1], reorderedPages[pageIndex]];

        const pageIds = reorderedPages.map((p) => p.id);
        await reorderPages({ sectionId: section.id, pageIds, courseId }).unwrap();
        enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      } catch (error) {
        console.error('Failed to move page down:', error);
        enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
      } finally {
        setMovingItem(null);
      }
    },
    [coursePages, section.id, reorderPages, courseId, t, closeContextMenu],
  );

  // ============================================
  // Context Menu Items Builder
  // ============================================

  const isQuiz = section.type === 'quiz';

  const getContextMenuItems = useCallback(() => {
    if (contextMenuType === 'section') {
      const items = [];
      if (!isQuiz) {
        items.push({ label: t('editCourse.addPage'), icon: 'Add', onClick: handleAddPage });
      }
      items.push(
        { label: t('editCourse.addSection'), icon: 'Add', onClick: handleAddSectionBelow },
        ...(quizEnabled ? [{ label: t('editCourse.addQuiz'), icon: 'Add', onClick: handleAddQuiz }] : []),
        { type: 'divider' },
        { label: t('editCourse.renameSection'), icon: 'Edit', onClick: handleRenameSection },
      );
      if (!isQuiz) {
        items.push({ label: t('editCourse.duplicateSection'), icon: 'Copy', onClick: handleDuplicateSection });
      }
      items.push(
        { label: t('editCourse.moveUp'), icon: 'ArrowUp', onClick: handleMoveSectionUp, disabled: sectionIndex === 0 },
        { label: t('editCourse.moveDown'), icon: 'ArrowDown', onClick: handleMoveSectionDown, disabled: sectionIndex === totalSections - 1 },
        { type: 'divider' },
        { label: t('editCourse.deleteSection'), icon: 'Delete', onClick: handleDeleteSection, danger: true },
      );
      return items;
    } else if (contextMenuType === 'page' && contextMenuTarget) {
      if (isQuiz) {
        return [{ label: t('editCourse.renamePage'), icon: 'Edit', onClick: () => handleRenamePage(contextMenuTarget) }];
      }

      const pages = coursePages.filter((p) => p.sectionId === section.id);
      const pageIndex = pages.findIndex((p) => p.id === contextMenuTarget.id);

      return [
        { label: t('editCourse.renamePage'), icon: 'Edit', onClick: () => handleRenamePage(contextMenuTarget) },
        { label: t('editCourse.duplicatePage'), icon: 'Copy', onClick: () => handleDuplicatePage(contextMenuTarget.id) },
        { label: t('editCourse.moveUp'), icon: 'ArrowUp', onClick: () => handleMovePageUp(contextMenuTarget.id), disabled: pageIndex === 0 },
        {
          label: t('editCourse.moveDown'),
          icon: 'ArrowDown',
          onClick: () => handleMovePageDown(contextMenuTarget.id),
          disabled: pageIndex === pages.length - 1,
        },
        { type: 'divider' },
        { label: t('editCourse.deletePage'), icon: 'Delete', onClick: () => handleDeletePage(contextMenuTarget), danger: true },
      ];
    }
    return [];
  }, [
    contextMenuType,
    contextMenuTarget,
    sectionIndex,
    totalSections,
    coursePages,
    section.id,
    isQuiz,
    quizEnabled,
    t,
    handleRenamePage,
    handleDuplicatePage,
    handleMovePageUp,
    handleMovePageDown,
    handleDeletePage,
    handleAddPage,
    handleAddSectionBelow,
    handleAddQuiz,
    handleRenameSection,
    handleDuplicateSection,
    handleMoveSectionUp,
    handleMoveSectionDown,
    handleDeleteSection,
  ]);

  return {
    // Context menu state
    contextMenu,
    contextMenuType,
    contextMenuTarget,

    // Moving item state
    movingItem,

    // Structure actions (for inline edit, delete dialog, etc.)
    structureActions,

    // Context menu handlers
    openSectionContextMenu,
    openPageContextMenu,
    closeContextMenu,
    getContextMenuItems,

    // Section actions
    handleMoveSectionUp,
    handleMoveSectionDown,
    handleRenameSection,
    handleDeleteSection,
    handleAddPage,
    handleAddSectionBelow,
    handleAddQuiz,
    handleDuplicateSection,

    // Page actions
    handleRenamePage,
    handleDeletePage,
    handleDuplicatePage,
    handleMovePageUp,
    handleMovePageDown,
  };
};

export default useSectionActions;
